import type { AdminHostedFaucet } from "../worker/services/admin-hosted";
import "./miner/runtime";
import { consumeVerification, readVerification, verificationKey } from "./verification";
import { getMiningRuntime } from "./mining";

interface PublicConfig {
  gatewayUrl: string;
  turnstileSiteKey: string;
  userShareBps: number | null;
  minimumPayoutLitoshi: number;
  sdrpPayoutEnabled: boolean;
  minimumSdrpPayout: string;
  faucetRewardLitoshi: number;
  faucetCooldownSeconds: number;
  faucetAdViewSeconds: number;
  faucetDailyLimit: number | null;
  faucetEnabled: boolean;
  autosurfEnabled: boolean;
  autosurfUserShareBps: number;
  autosurfLitoshiPerZer: number | null;
}

interface Account {
  faucetpayDestination: string | null;
}

interface Balance {
  pendingWork: number;
  pendingEstimateLitoshi: number | null;
  confirmedLifetimeLitoshi: number;
  withdrawableLitoshi: number;
  paidLifetimeLitoshi: number;
}

interface SdrpQuote {
  id: string;
  destination: string;
  amountLitoshi: number;
  grossSdrp: string;
  networkFeeSdrp: string;
  networkFeeEth: string;
  netSdrp: string;
  ltcUsd: string;
  ethUsd: string;
  sdrpUsd: "1.00";
  priceSource: string;
  createdAt: number;
  expiresAt: number;
}

interface SdrpPayout {
  id: string;
  amountLitoshi: number;
  destination: string;
  grossSdrp: string;
  networkFeeSdrp: string;
  netSdrp: string;
  status: "processing" | "broadcast" | "succeeded" | "failed" | "operator_review";
  transactionHash: string | null;
  failureReason: string | null;
}

interface FaucetClaim {
  id: string;
  rewardLitoshi: number;
  status: "processing" | "succeeded" | "operator_review";
  faucetpayPayoutId: string | null;
  failureReason: string | null;
  createdAt: number;
  completedAt: number | null;
}

interface FaucetAdView {
  id: string;
  startedAt: number;
  unlockAt: number;
  expiresAt: number;
}

interface FaucetSummary {
  rewardLitoshi: number;
  cooldownSeconds: number;
  adViewSeconds: number;
  dailyLimit: number | null;
  remainingVisits: number | null;
  nextClaimAt: number | null;
  nextAdOpenAt: number | null;
  activeAdView: FaucetAdView | null;
  claimCount: number;
  paidLifetimeLitoshi: number;
  claims: FaucetClaim[];
}

interface AutosurfCredit {
  id: string;
  providerAmountZer: string;
  rewardLitoshi: number;
  createdAt: number;
}

interface AutosurfSummary {
  enabled: boolean;
  creditCount: number;
  creditedLifetimeLitoshi: number;
  fractionalLitoshi: number;
  lastCreditAt: number | null;
  credits: AutosurfCredit[];
}

interface AdminAdjustment {
  id: string;
  amountSdrpWei: string;
  note: string;
  createdAt: number;
}

interface AdminDashboard {
  hostedFaucets: AdminHostedFaucet[];
  generatedAt: number;
  circulation: {
    totalOutstandingSdrpWei: string;
    siteSdrpPayoutsWei: string;
    uniswapNetBoughtWei: string;
    uniswapGrossBoughtWei: string;
    uniswapGrossSoldWei: string;
    manualAdjustmentsWei: string;
    complete: boolean;
  };
  revenue: {
    hostedMicrolitoshi: string;
    totalMicrolitoshi: string;
    miningMicrolitoshi: string;
    autosurfMicrolitoshi: string;
    scope: string;
  };
  userEarnings: {
    hostedMicrolitoshi: string;
    totalMicrolitoshi: string;
    miningMicrolitoshi: string;
    autosurfMicrolitoshi: string;
  };
  profit: {
    hostedFeeMicrolitoshi: string;
    grossMarginMicrolitoshi: string;
    dailyFaucetExpenseMicrolitoshi: string;
    ownerAccountAddbackMicrolitoshi: string;
    totalMicrolitoshi: string;
  };
  activity: {
    miningSettlementCount: number;
    zeradsReportCount: number;
    successfulDailyFaucetClaimCount: number;
    lastEarningAt: number | null;
    operatorReviewCount: number;
  };
  uniswap: {
    poolAddress: string;
    startBlock: number;
    nextBlock: number;
    indexedThroughBlock: number | null;
    latestConfirmedBlock: number | null;
    swapCount: number;
    lastSuccessAt: number | null;
    lastError: string | null;
    caughtUp: boolean;
  };
  adjustments: AdminAdjustment[];
}

type PageName = "home" | "miner" | "faucet" | "autosurf" | "sdrp" | "swap" | "admin";

interface TurnstileApi {
  render: (target: string, options: Record<string, unknown>) => string;
  reset: (widgetId: string) => void;
  remove: (widgetId: string) => void;
}

const storageKey = "silverdrop.account.token.v1";
let token = localStorage.getItem(storageKey);
let settings: PublicConfig;
let startTurnstileToken = "";
let payoutTurnstileToken = "";
let activeSessionId: string | null = null;
let turnstileApi: TurnstileApi | null = null;
let faucetTurnstileToken = "";
let miningTurnstileWidget: string | null = null;
let faucetTurnstileWidget: string | null = null;
let activeFaucetAdView: FaucetAdView | null = null;
let faucetActionBusy = false;
let faucetCountdownTimer: number | null = null;
let activeSdrpQuote: SdrpQuote | null = null;
let sdrpQuoteGeneration = 0;
let sdrpQuoteTimer: number | null = null;
let sdrpQuoteExpiryTimer: number | null = null;

const byId = <T extends HTMLElement>(id: string): T => document.getElementById(id) as T;
const faucetAdUrl = "https://ltcdrop.com/poplix";

class ApiRequestError extends Error {
  constructor(
    message: string,
    readonly code?: string,
    readonly details?: unknown
  ) {
    super(message);
  }
}

function loadTraffic2Bitcoin(faucetPage: boolean): void {
  const iframe = document.createElement("iframe");
  iframe.src = "https://traffic2bitcoin.com/ptp.php?ref=76478&sitetype=1";
  iframe.width = "0";
  iframe.height = "0";
  iframe.scrolling = "no";
  iframe.frameBorder = "0";
  iframe.tabIndex = -1;
  iframe.title = "Traffic2Bitcoin";
  byId(faucetPage ? "faucet-traffic2bitcoin" : "mining-traffic2bitcoin").append(iframe);
}

async function api<T>(path: string, init: RequestInit = {}): Promise<T> {
  const headers = new Headers(init.headers);
  headers.set("accept", "application/json");
  if (init.body) headers.set("content-type", "application/json");
  if (token) headers.set("authorization", `Bearer ${token}`);
  const response = await fetch(path, { ...init, headers });
  const data = await response.json() as T & {
    error?: { code?: string; message?: string; details?: unknown };
  };
  if (!response.ok) {
    throw new ApiRequestError(
      data.error?.message ?? `Request failed (${response.status})`,
      data.error?.code,
      data.error?.details
    );
  }
  return data;
}

function ltc(litoshi: number): string {
  const whole = Math.floor(litoshi / 100_000_000);
  const fraction = String(litoshi % 100_000_000).padStart(8, "0");
  return `${whole}.${fraction} LTC`;
}

function decimalAmount(value: string, maximumFractionDigits = 8): string {
  const [whole = "0", fraction = ""] = value.split(".");
  const trimmed = fraction.slice(0, maximumFractionDigits).replace(/0+$/u, "");
  return trimmed ? `${whole}.${trimmed}` : whole;
}

function formatAtomic(value: string, decimals: number, maximumFractionDigits: number, suffix: string): string {
  const amount = BigInt(value);
  const negative = amount < 0n;
  const absolute = negative ? -amount : amount;
  const scale = 10n ** BigInt(decimals);
  const whole = absolute / scale;
  const fraction = (absolute % scale)
    .toString()
    .padStart(decimals, "0")
    .slice(0, maximumFractionDigits)
    .replace(/0+$/u, "");
  const number = `${negative ? "-" : ""}${whole.toLocaleString()}${fraction ? `.${fraction}` : ""}`;
  return suffix ? `${number} ${suffix}` : number;
}

function formatAdminLtc(microlitoshi: string): string {
  return formatAtomic(microlitoshi, 14, 8, "LTC");
}

function formatAdminSdrp(wei: string): string {
  return formatAtomic(wei, 18, 6, "SDRP");
}

async function ensureAccount(): Promise<void> {
  if (token) {
    try {
      await api("/api/account");
      return;
    } catch (error) {
      if (!(error instanceof ApiRequestError) || error.code !== "unauthorized") throw error;
      localStorage.removeItem(storageKey);
      token = null;
    }
  }
  const account = await api<{ token: string }>("/api/account", { method: "POST" });
  token = account.token;
  localStorage.setItem(storageKey, token);
}

async function getAccount(): Promise<Account> {
  return api<Account>("/api/account");
}

async function refreshBalance(): Promise<Balance> {
  const balance = await api<Balance>("/api/balance");
  byId("pending").textContent = balance.pendingEstimateLitoshi === null ? "Awaiting first settlement" : `≈ ${ltc(balance.pendingEstimateLitoshi)}`;
  byId("confirmed").textContent = ltc(balance.confirmedLifetimeLitoshi);
  byId("withdrawable").textContent = ltc(balance.withdrawableLitoshi);
  byId("paid").textContent = ltc(balance.paidLifetimeLitoshi);
  byId("work").textContent = `${balance.pendingWork.toLocaleString()} units`;
  return balance;
}

function setupThreads(): void {
  const select = byId<HTMLSelectElement>("threads");
  const maximum = Math.max(1, Math.min(navigator.hardwareConcurrency || 2, 8));
  for (let value = 1; value <= maximum; value += 1) {
    const option = document.createElement("option");
    option.value = String(value);
    option.textContent = String(value);
    if (value === Math.min(2, maximum)) option.selected = true;
    select.append(option);
  }
}

async function getTurnstileApi(): Promise<TurnstileApi> {
  if (!settings.turnstileSiteKey || settings.turnstileSiteKey.startsWith("REPLACE_")) {
    throw new Error("Cloudflare human verification is not configured.");
  }
  if (turnstileApi) return turnstileApi;
  await new Promise<void>((resolve, reject) => {
    const script = document.createElement("script");
    script.src = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";
    script.async = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error("Could not load Turnstile"));
    document.head.append(script);
  });
  turnstileApi = (window as unknown as { turnstile: TurnstileApi }).turnstile;
  return turnstileApi;
}

async function setupInlineVerification(
  target: string,
  action: string,
  setToken: (value: string) => void,
  messageId: string
): Promise<string> {
  const api = await getTurnstileApi();
  return api.render(target, {
    sitekey: settings.turnstileSiteKey,
    action,
    theme: "dark",
    callback: setToken,
    "expired-callback": () => setToken(""),
    "error-callback": () => {
      setToken("");
      byId(messageId).textContent = "Verification could not load. Please try again.";
    }
  });
}

let savedDestination = "";
function takeVerification(action: string): string {
  return consumeVerification(sessionStorage, action, token);
}
async function setupHomePage(): Promise<void> {
  const account = await getAccount();
  byId<HTMLInputElement>("destination").value = account.faucetpayDestination ?? "";
  let homeDestination = account.faucetpayDestination ?? "";
  const message = byId("home-message");
  message.textContent = account.faucetpayDestination ? `Saved destination: ${account.faucetpayDestination}` : "Save a destination for FaucetPay payouts and daily claims.";
  byId("home-account-form").addEventListener("submit", event => {
    event.preventDefault();
    const button = byId("home-account-form").querySelector<HTMLButtonElement>("button")!;
    button.disabled = true;
    void saveDestination("destination").then(value => {
      homeDestination = value;
      message.textContent = `Saved and verified: ${value}`;
    }).catch((error: Error) => { message.textContent = error.message; }).finally(() => { button.disabled = false; });
  });
  const select = byId<HTMLSelectElement>("home-action");
  const requested = new URLSearchParams(location.search).get("verify");
  if (["start_mining", "claim_faucet", "request_payout", "autosurf"].includes(requested ?? "")) select.value = requested!;
  const button = byId<HTMLButtonElement>("home-continue");
  let widget: string | null = null;
  let response = "";
  let expires = 0;
  let generation = 0;
  const render = async () => {
    const current = ++generation;
    response = "";
    if (widget && turnstileApi) turnstileApi.remove(widget);
    widget = null;
    button.disabled = select.value !== "autosurf";
    button.textContent = select.value === "autosurf" ? "Continue to autosurf ↗" : "Verify to continue";
    byId("verification-note").textContent = select.value === "autosurf" ? "ZerAds handles verification in its own surf window." : "Verification is single-use and expires after a few minutes. Continue when you’re ready.";
    if (select.value === "autosurf") return;
    const api = await getTurnstileApi();
    if (current !== generation) return;
    widget = api.render("#home-turnstile", {
      sitekey: settings.turnstileSiteKey, action: select.value, theme: "dark",
      callback: (value: string) => { response = value; expires = Date.now() + 240_000; button.disabled = false; button.textContent = "Continue ↗"; },
      "expired-callback": () => { response = ""; button.disabled = true; button.textContent = "Verify again to continue"; },
      "error-callback": () => { response = ""; button.disabled = true; message.textContent = "Verification could not load. Please try again."; }
    });
  };
  select.addEventListener("change", () => void render().catch((error: Error) => { message.textContent = error.message; }));
  button.addEventListener("click", () => {
    const action = select.value;
    if (action === "claim_faucet" && !homeDestination) { message.textContent = "Verify and save your FaucetPay destination before continuing to the daily claim."; return; }
    if (action !== "autosurf") {
      if (!response || expires <= Date.now()) { message.textContent = "Verification expired. Please verify again."; void render().catch((error: Error) => { message.textContent = error.message; }); return; }
      sessionStorage.setItem(`${verificationKey}.${action}`, JSON.stringify({ token: response, account: token, expires }));
    }
    location.assign(action === "claim_faucet" ? "/faucet" : action === "autosurf" ? "/autosurf" : "/mining");
  });
  await render();
}

async function startMining(): Promise<void> {
  const message = byId("mining-message");
  if (!byId<HTMLInputElement>("consent").checked) throw new Error("Confirm the mining consent statement first.");
  startTurnstileToken ||= takeVerification("start_mining");
  if (!startTurnstileToken) throw new Error("Complete the security check on this page before starting mining.");
  const runtime = getMiningRuntime();
  if (!runtime) throw new Error("The audited browser hashing runtime has not been installed on this deployment yet.");
  const startButton = byId<HTMLButtonElement>("start");
  startButton.disabled = true;
  try {
    const turnstileToken = startTurnstileToken;
    startTurnstileToken = "";
    takeVerification("start_mining");
    const session = await api<{ sessionId: string; gatewayUrl: string; ticket: string }>("/api/mining/sessions", {
      method: "POST",
      body: JSON.stringify({ turnstileToken })
    });
    activeSessionId = session.sessionId;
    await runtime.start({
      gatewayUrl: session.gatewayUrl,
      ticket: session.ticket,
      threads: Number(byId<HTMLSelectElement>("threads").value),
      cpuPercent: Number(byId<HTMLInputElement>("cpu").value),
      onStatus: (status) => { message.textContent = status; },
      onHashrate: (rate) => { byId("mining-state").textContent = `${rate.toFixed(1)} H/s`; },
      onAcceptedShare: () => { void refreshBalance(); },
      onStopped: (reason) => { void handleUnexpectedStop(reason); }
    });
    byId("mining-state").textContent = "Connected";
    byId<HTMLButtonElement>("stop").disabled = false;
  } catch (error) {
    if (activeSessionId) await api(`/api/mining/sessions/${activeSessionId}/stop`, { method: "POST" }).catch(() => undefined);
    activeSessionId = null;
    startButton.disabled = false;
    throw error;
  } finally {
    if (miningTurnstileWidget !== null) turnstileApi?.reset(miningTurnstileWidget);
  }
}

async function handleUnexpectedStop(_reason: string): Promise<void> {
  if (activeSessionId) await api(`/api/mining/sessions/${activeSessionId}/stop`, { method: "POST" }).catch(() => undefined);
  activeSessionId = null;
  byId("mining-state").textContent = "Stopped";
  byId<HTMLButtonElement>("start").disabled = false;
  byId<HTMLButtonElement>("stop").disabled = true;
  await refreshBalance().catch(() => undefined);
}

async function stopMining(): Promise<void> {
  await getMiningRuntime()?.stop();
  if (activeSessionId) await api(`/api/mining/sessions/${activeSessionId}/stop`, { method: "POST" });
  activeSessionId = null;
  byId("mining-state").textContent = "Stopped";
  byId<HTMLButtonElement>("start").disabled = false;
  byId<HTMLButtonElement>("stop").disabled = true;
  await refreshBalance();
}

async function saveDestination(inputId: string): Promise<string> {
  const destination = byId<HTMLInputElement>(inputId).value;
  const result = await api<{ faucetpayDestination: string }>("/api/account/destination", {
    method: "PUT",
    body: JSON.stringify({ destination })
  });
  return result.faucetpayDestination;
}

function updateSdrpConfirmState(): void {
  const button = document.getElementById("confirm-sdrp") as HTMLButtonElement | null;
  if (button) button.disabled = !activeSdrpQuote || !payoutTurnstileToken;
}

function clearSdrpQuote(): void {
  activeSdrpQuote = null;
  sdrpQuoteGeneration += 1;
  if (sdrpQuoteTimer !== null) window.clearTimeout(sdrpQuoteTimer);
  if (sdrpQuoteExpiryTimer !== null) window.clearTimeout(sdrpQuoteExpiryTimer);
  sdrpQuoteTimer = null;
  sdrpQuoteExpiryTimer = null;
  byId("sdrp-quote").hidden = true;
  updateSdrpConfirmState();
}

function showWithdrawChoice(): void {
  clearSdrpQuote();
  const dialog = byId<HTMLDialogElement>("withdraw-dialog");
  dialog.setAttribute("aria-labelledby", "withdraw-dialog-title");
  byId("withdraw-choice").hidden = false;
  byId("withdraw-sdrp-step").hidden = true;
  byId("sdrp-dialog-message").textContent = "";
  byId<HTMLInputElement>("sdrp-destination").value = "";
  byId("sdrp-address-help").classList.remove("error");
  byId("sdrp-address-help").textContent = `Minimum withdrawal: ${settings.minimumSdrpPayout} SDRP.`;
}

function updateWithdrawAdvertisement(): void {
  const dialog = byId<HTMLDialogElement>("withdraw-dialog");
  const slot = byId("withdraw-ad-slot");
  const card = slot.parentElement!;
  const style = getComputedStyle(card);
  const availableWidth = card.clientWidth - parseFloat(style.paddingLeft) - parseFloat(style.paddingRight);
  // Never request a hidden or clipped fixed-size banner, or refresh it while open.
  const canShow = dialog.open && availableWidth >= 468;
  for (const [slotId, templateId] of [
    ["withdraw-ad-slot", "withdraw-ad-template"]
  ] as const) {
    const adSlot = byId(slotId);
    adSlot.hidden = !canShow;
    if (!canShow) adSlot.replaceChildren();
    else if (!adSlot.firstElementChild) {
      adSlot.append(byId<HTMLTemplateElement>(templateId).content.cloneNode(true));
    }
  }
}

function openWithdrawDialog(): void {
  showWithdrawChoice();
  const dialog = byId<HTMLDialogElement>("withdraw-dialog");
  if (!dialog.open) {
    if (typeof dialog.showModal === "function") dialog.showModal();
    else dialog.setAttribute("open", "");
  }
  updateWithdrawAdvertisement();
  byId<HTMLButtonElement>("withdraw-faucetpay").focus({ preventScroll: true });
  byId("withdraw-ad-slot").parentElement!.scrollTop = 0;
}

function closeWithdrawDialog(): void {
  clearSdrpQuote();
  const dialog = byId<HTMLDialogElement>("withdraw-dialog");
  if (dialog.open) {
    if (typeof dialog.close === "function") dialog.close();
    else dialog.removeAttribute("open");
  }
  updateWithdrawAdvertisement();
}

function showSdrpStep(): void {
  const dialog = byId<HTMLDialogElement>("withdraw-dialog");
  dialog.setAttribute("aria-labelledby", "sdrp-dialog-title");
  byId("withdraw-choice").hidden = true;
  byId("withdraw-sdrp-step").hidden = false;
  const help = byId("sdrp-address-help");
  help.classList.remove("error");
  help.textContent = `Minimum withdrawal: ${settings.minimumSdrpPayout} SDRP. Enter an address for an exact fee quote.`;
  if (!settings.sdrpPayoutEnabled) {
    help.classList.add("error");
    help.textContent = "SDRP withdrawals are currently unavailable.";
    byId<HTMLInputElement>("sdrp-destination").disabled = true;
  } else {
    byId<HTMLInputElement>("sdrp-destination").disabled = false;
  }
  if (!payoutTurnstileToken) {
    byId("sdrp-dialog-message").textContent = "Complete withdrawal verification on the homepage before confirming.";
  }
  byId<HTMLInputElement>("sdrp-destination").focus();
}

async function loadSdrpQuote(destination: string, generation: number): Promise<void> {
  try {
    const result = await api<{ quote: SdrpQuote }>("/api/sdrp/quote", {
      method: "POST",
      body: JSON.stringify({ destination })
    });
    if (generation !== sdrpQuoteGeneration) return;
    const currentDestination = byId<HTMLInputElement>("sdrp-destination").value.trim();
    if (currentDestination.toLowerCase() !== result.quote.destination.toLowerCase()) return;
    activeSdrpQuote = result.quote;
    byId("sdrp-gross").textContent = `${decimalAmount(result.quote.grossSdrp)} SDRP`;
    byId("sdrp-fee").textContent = `${decimalAmount(result.quote.networkFeeSdrp)} SDRP`;
    byId("sdrp-net").textContent = `${decimalAmount(result.quote.netSdrp)} SDRP`;
    byId("sdrp-rates").textContent = `LTC $${result.quote.ltcUsd} · ETH $${result.quote.ethUsd}`;
    byId("sdrp-quote").hidden = false;
    const help = byId("sdrp-address-help");
    help.classList.remove("error");
    help.textContent = `Quote from ${result.quote.priceSource}; locked until ${new Date(result.quote.expiresAt * 1_000).toLocaleTimeString()}.`;
    byId<HTMLInputElement>("sdrp-destination").setAttribute("aria-invalid", "false");
    if (!payoutTurnstileToken) {
      byId("sdrp-dialog-message").textContent = "Quote ready. Complete withdrawal verification on the homepage before confirming.";
    } else {
      byId("sdrp-dialog-message").textContent = "Review the amount and address, then confirm the irreversible transfer.";
    }
    updateSdrpConfirmState();
    const millisecondsUntilExpiry = Math.max(0, result.quote.expiresAt * 1_000 - Date.now());
    sdrpQuoteExpiryTimer = window.setTimeout(() => {
      if (activeSdrpQuote?.id !== result.quote.id) return;
      clearSdrpQuote();
      byId("sdrp-address-help").classList.add("error");
      byId("sdrp-address-help").textContent = "This quote expired. Edit or re-enter the address to calculate a new one.";
    }, millisecondsUntilExpiry);
  } catch (error) {
    if (generation !== sdrpQuoteGeneration) return;
    clearSdrpQuote();
    const help = byId("sdrp-address-help");
    help.classList.add("error");
    help.textContent = error instanceof Error ? error.message : "The SDRP quote could not be calculated.";
    byId<HTMLInputElement>("sdrp-destination").setAttribute("aria-invalid", "true");
  }
}

function handleSdrpAddressInput(): void {
  clearSdrpQuote();
  const input = byId<HTMLInputElement>("sdrp-destination");
  const destination = input.value.trim();
  const help = byId("sdrp-address-help");
  help.classList.remove("error");
  if (!destination) {
    input.removeAttribute("aria-invalid");
    help.textContent = `Minimum withdrawal: ${settings.minimumSdrpPayout} SDRP. Enter an address for an exact fee quote.`;
    return;
  }
  if (!/^0x[0-9a-f]{40}$/iu.test(destination)) {
    input.setAttribute("aria-invalid", "true");
    help.classList.add("error");
    help.textContent = "Enter a complete 42-character Base wallet address beginning with 0x.";
    return;
  }
  input.setAttribute("aria-invalid", "false");
  help.textContent = "Calculating live LTC and ETH prices and the Base network fee…";
  const generation = sdrpQuoteGeneration;
  sdrpQuoteTimer = window.setTimeout(() => void loadSdrpQuote(destination, generation), 450);
}

async function withdrawWithFaucetPay(): Promise<void> {
  payoutTurnstileToken = takeVerification("request_payout");
  if (!payoutTurnstileToken) throw new Error("Complete withdrawal verification on the homepage first.");
  const button = byId<HTMLButtonElement>("withdraw-faucetpay");
  button.disabled = true;
  const turnstileToken = payoutTurnstileToken;
  payoutTurnstileToken = "";
  try {
    const result = await api<{ payout: { status: string; amountLitoshi: number } }>("/api/payouts", {
      method: "POST",
      headers: { "idempotency-key": crypto.randomUUID().replaceAll("-", "") },
      body: JSON.stringify({ turnstileToken })
    });
    byId("payout-message").textContent = `${ltc(result.payout.amountLitoshi)} payout: ${result.payout.status}`;
    closeWithdrawDialog();
    await refreshBalance();
  } finally {
    button.disabled = false;

  }
}

async function confirmSdrpWithdrawal(): Promise<void> {
  const quote = activeSdrpQuote;
  if (!quote) throw new Error("Enter a valid Base wallet address and wait for the fee quote.");
  if (quote.expiresAt * 1_000 <= Date.now()) throw new Error("The SDRP quote expired; request a new quote.");
  payoutTurnstileToken = takeVerification("request_payout");
  if (!payoutTurnstileToken) throw new Error("Complete withdrawal verification on the homepage before confirming.");
  const button = byId<HTMLButtonElement>("confirm-sdrp");
  const turnstileToken = payoutTurnstileToken;
  payoutTurnstileToken = "";
  button.disabled = true;
  byId("sdrp-dialog-message").textContent = "Signing and submitting your SDRP transfer…";
  try {
    const result = await api<{ payout: SdrpPayout }>("/api/sdrp/payouts", {
      method: "POST",
      headers: { "idempotency-key": crypto.randomUUID().replaceAll("-", "") },
      body: JSON.stringify({ quoteId: quote.id, turnstileToken })
    });
    const status = result.payout.status === "succeeded" ? "confirmed" : "submitted";
    byId("payout-message").textContent = `${decimalAmount(result.payout.netSdrp)} SDRP ${status} to ${result.payout.destination}.`;
    closeWithdrawDialog();
    await refreshBalance();
  } finally {

  }
}

function setupWithdrawDialog(): void {
  const dialog = byId<HTMLDialogElement>("withdraw-dialog");
  byId("withdraw-dialog-close").addEventListener("click", closeWithdrawDialog);
  byId("withdraw-back").addEventListener("click", showWithdrawChoice);
  byId("withdraw-sdrp").addEventListener("click", showSdrpStep);
  byId("withdraw-faucetpay").addEventListener("click", () => void withdrawWithFaucetPay().catch((error: Error) => {
    closeWithdrawDialog();
    byId("payout-message").textContent = error.message;
  }));
  byId("confirm-sdrp").addEventListener("click", () => void confirmSdrpWithdrawal().catch((error: Error) => {
    byId("sdrp-dialog-message").textContent = error.message;
    updateSdrpConfirmState();
  }));
  byId("sdrp-destination").addEventListener("input", handleSdrpAddressInput);
  dialog.addEventListener("close", () => {
    clearSdrpQuote();
    updateWithdrawAdvertisement();
  });
  new ResizeObserver(updateWithdrawAdvertisement).observe(byId("withdraw-ad-slot").parentElement!);
}

async function setupMiningPage(): Promise<void> {
  const account = await getAccount();
  byId("mining-destination").textContent = account.faucetpayDestination ?? "No FaucetPay destination saved yet.";
  payoutTurnstileToken = readVerification(sessionStorage, "request_payout", token);
  await refreshBalance().catch((error: Error) => { byId("mining-message").textContent = error.message; });
  byId("refresh").addEventListener("click", () => void refreshBalance());
  byId("start").addEventListener("click", () => void startMining().catch((error: Error) => {
    byId("mining-message").textContent = error.message;
  }));
  byId("stop").addEventListener("click", () => void stopMining().catch((error: Error) => {
    byId("mining-message").textContent = error.message;
  }));
  byId("payout").addEventListener("click", openWithdrawDialog);
  miningTurnstileWidget = await setupInlineVerification(
    "#mining-turnstile", "start_mining", value => { startTurnstileToken = value; }, "mining-message"
  );
}

function claimStatusLabel(claim: FaucetClaim): string {
  if (claim.status === "operator_review" && !claim.faucetpayPayoutId) return "Not paid · review";
  if (claim.status === "operator_review") return "Needs review";
  return claim.status;
}

function renderClaims(claims: FaucetClaim[]): void {
  const container = byId("faucet-claims");
  container.replaceChildren();
  if (claims.length === 0) {
    const empty = document.createElement("p");
    empty.className = "empty-state";
    empty.textContent = "Your rewarded faucet payouts will appear here.";
    container.append(empty);
    return;
  }
  for (const claim of claims) {
    const row = document.createElement("div");
    row.className = "claim-row";
    const title = document.createElement("div");
    title.className = "claim-title";
    const name = document.createElement("strong");
    name.textContent = "Rewarded faucet claim";
    const time = document.createElement("time");
    time.dateTime = new Date(claim.createdAt * 1_000).toISOString();
    time.textContent = new Date(claim.createdAt * 1_000).toLocaleString();
    title.append(name, time);
    if (claim.failureReason) {
      const details = document.createElement("span");
      details.className = "claim-details";
      details.textContent = claim.failureReason;
      title.append(details);
    }
    const amount = document.createElement("span");
    amount.className = "claim-amount";
    amount.textContent = ltc(claim.rewardLitoshi);
    const status = document.createElement("span");
    status.className = `claim-status ${claim.status}`;
    status.textContent = claimStatusLabel(claim);
    row.append(title, amount, status);
    container.append(row);
  }
}

function clearFaucetCountdown(): void {
  if (faucetCountdownTimer !== null) window.clearTimeout(faucetCountdownTimer);
  faucetCountdownTimer = null;
}

let faucetNextClaimAt: number | null = null;

function renderFaucetAction(): void {
  clearFaucetCountdown();
  const claimButton = byId<HTMLButtonElement>("claim-faucet");
  const now = Date.now();
  if (activeFaucetAdView && activeFaucetAdView.expiresAt <= now) activeFaucetAdView = null;

  if (faucetActionBusy) {
    claimButton.disabled = true;
    return;
  }

  if (faucetNextClaimAt && faucetNextClaimAt > now) {
    claimButton.disabled = true;
    claimButton.textContent = "Available after 24 hours";
    byId("faucet-current-state").textContent = "Daily limit reached";
    byId("faucet-next-claim").textContent = new Date(faucetNextClaimAt).toLocaleString();
    faucetCountdownTimer = window.setTimeout(renderFaucetAction, Math.min(60_000, faucetNextClaimAt - now));
    return;
  }

  if (activeFaucetAdView) {
    const remainingMs = activeFaucetAdView.unlockAt - now;
    if (remainingMs > 0) {
      const remainingSeconds = Math.ceil(remainingMs / 1_000);
      claimButton.disabled = true;
      claimButton.textContent = `Ad open — ${remainingSeconds}s remaining`;
      byId("faucet-current-state").textContent = "Viewing ad";
      byId("faucet-next-claim").textContent = "Once per IP every 24 hours";
      faucetCountdownTimer = window.setTimeout(renderFaucetAction, Math.min(250, remainingMs));
      return;
    }
    claimButton.disabled = !settings.faucetEnabled;
    claimButton.textContent = "Claim Reward";
    byId("faucet-current-state").textContent = "Ready to claim";
    byId("faucet-next-claim").textContent = "Once per IP every 24 hours";
    return;
  }

  claimButton.disabled = true;
  claimButton.textContent = "View an ad first";
  byId("faucet-current-state").textContent = "Ready";
  byId("faucet-next-claim").textContent = "Once per IP every 24 hours";
}

async function refreshFaucetClaims(): Promise<FaucetSummary> {
  const summary = await api<FaucetSummary>("/api/faucet/claims");
  byId("faucet-paid").textContent = ltc(summary.paidLifetimeLitoshi);
  byId("faucet-claim-count").textContent = summary.claimCount.toLocaleString();
  renderClaims(summary.claims);
  activeFaucetAdView = summary.activeAdView;
  faucetNextClaimAt = summary.nextClaimAt;
  renderFaucetAction();
  return summary;
}

async function openFaucetAd(): Promise<void> {
  // Every click opens the sponsor, independently of reward eligibility.
  const adTab = window.open(faucetAdUrl, "_blank");
  if (!adTab) throw new Error("Allow pop-ups for Silverdrop so the rewarded ad can open in a new tab.");
  try {
    adTab.opener = null;
    adTab.focus();
  } catch { /* The cross-origin tab may already be loading. */ }

  // Reopening an ad does not reset an active timer or bypass claim limits.
  if (faucetActionBusy || (activeFaucetAdView && activeFaucetAdView.expiresAt > Date.now())) return;
  if (!settings.faucetEnabled) return;
  const destination = savedDestination;
  if (!destination) throw new Error("Save your FaucetPay destination on the homepage before claiming a reward.");

  faucetActionBusy = true;
  const claimButton = byId<HTMLButtonElement>("claim-faucet");
  claimButton.textContent = "Opening ad…";
  byId("faucet-current-state").textContent = "Opening ad";
  byId("faucet-message").textContent = "Keep the sponsor tab open for at least 5 seconds, then return here to claim.";
  renderFaucetAction();

  try {
    const result = await api<{
      adView: FaucetAdView;
      remainingVisits: number | null;
      nextClaimAt: number | null;
      replay: boolean;
    }>("/api/faucet/ad-views", {
      method: "POST",
      body: JSON.stringify({ destination })
    });
    activeFaucetAdView = result.adView;
  } catch (error) {
    // Keep the sponsor tab open; report reward eligibility errors on this page.
    throw error;
  } finally {
    faucetActionBusy = false;
    renderFaucetAction();
  }
}

async function claimFromFaucet(): Promise<void> {
  if (!activeFaucetAdView || activeFaucetAdView.unlockAt > Date.now()) {
    throw new Error("View the rewarded ad for the full five seconds first.");
  }
  faucetTurnstileToken ||= takeVerification("claim_faucet");
  if (!faucetTurnstileToken) throw new Error("Complete the security check on this page before claiming your reward.");
  const destination = savedDestination;
  if (!destination) throw new Error("Save your FaucetPay destination on the homepage first.");

  const claimButton = byId<HTMLButtonElement>("claim-faucet");
  const turnstileToken = faucetTurnstileToken;
  const adViewId = activeFaucetAdView.id;
  faucetTurnstileToken = "";
  takeVerification("claim_faucet");
  faucetActionBusy = true;
  claimButton.disabled = true;
  claimButton.textContent = "Sending reward…";
  byId("faucet-current-state").textContent = "Sending";
  byId("faucet-message").textContent = "Verifying your account and calculating your reward…";

  try {
    const result = await api<{
      claim: FaucetClaim;
      remainingVisits: number | null;
      nextClaimAt: number | null;
    }>("/api/faucet/claims", {
      method: "POST",
      body: JSON.stringify({ destination, turnstileToken, adViewId })
    });
    activeFaucetAdView = null;
    byId("faucet-current-state").textContent = result.claim.status === "succeeded" ? "Paid" : claimStatusLabel(result.claim);
    byId("faucet-message").textContent = result.claim.status === "succeeded"
      ? `${result.claim.rewardLitoshi} litoshi sent to FaucetPay. You can claim again from this IP in 24 hours.`
      : result.claim.failureReason ?? "Your payout is being processed.";
    await refreshFaucetClaims();
  } finally {
    if (faucetTurnstileWidget !== null) turnstileApi?.reset(faucetTurnstileWidget);
    faucetActionBusy = false;
    renderFaucetAction();
  }
}

async function setupFaucetPage(): Promise<void> {
  byId("faucet-reward").textContent = "$0.000001 in LTC · minimum 1 litoshi";
  const account = await getAccount();
  savedDestination = account.faucetpayDestination ?? "";
  byId("faucet-account").textContent = savedDestination || "Save your FaucetPay destination on the homepage first.";
  await refreshFaucetClaims();

  byId("view-faucet-ad").addEventListener("click", () => {
    void openFaucetAd().catch((error: Error) => {
      byId("faucet-message").textContent = error.message;
      void refreshFaucetClaims().catch(() => renderFaucetAction());
    });
  });
  byId("claim-faucet").addEventListener("click", () => {
    void claimFromFaucet().catch((error: Error) => {
      const nextVisitAt = error instanceof ApiRequestError
        && typeof (error.details as { nextVisitAt?: unknown } | undefined)?.nextVisitAt === "number"
        ? (error.details as { nextVisitAt: number }).nextVisitAt
        : null;
      byId("faucet-message").textContent = nextVisitAt
        ? `${error.message}. Next visit: ${new Date(nextVisitAt * 1_000).toLocaleString()}.`
        : error.message;
      void refreshFaucetClaims().catch(() => renderFaucetAction());
    });
  });
  byId("refresh-faucet").addEventListener("click", () => void refreshFaucetClaims()
    .catch((error: Error) => { byId("faucet-message").textContent = error.message; }));
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "visible") {
      renderFaucetAction();
      void refreshFaucetClaims().catch(() => undefined);
    }
  });
  faucetTurnstileWidget = await setupInlineVerification(
    "#faucet-turnstile", "claim_faucet", value => { faucetTurnstileToken = value; }, "faucet-message"
  );
}

function renderAutosurfCredits(credits: AutosurfCredit[]): void {
  const container = byId("autosurf-credits");
  container.replaceChildren();
  if (credits.length === 0) {
    const empty = document.createElement("p");
    empty.className = "empty-state";
    empty.textContent = "Completed ZerAds views will appear here after the provider reports them.";
    container.append(empty);
    return;
  }
  for (const credit of credits) {
    const row = document.createElement("div");
    row.className = "claim-row";
    const title = document.createElement("div");
    title.className = "claim-title";
    const name = document.createElement("strong");
    name.textContent = "ZerAds PTC earnings";
    const time = document.createElement("time");
    time.dateTime = new Date(credit.createdAt * 1_000).toISOString();
    time.textContent = new Date(credit.createdAt * 1_000).toLocaleString();
    title.append(name, time);
    const amount = document.createElement("span");
    amount.className = "claim-amount";
    amount.textContent = credit.rewardLitoshi > 0 ? `+ ${ltc(credit.rewardLitoshi)}` : "Carried forward";
    const providerAmount = document.createElement("span");
    providerAmount.className = "claim-status succeeded";
    providerAmount.textContent = `${credit.providerAmountZer} ZER`;
    row.append(title, amount, providerAmount);
    container.append(row);
  }
}

async function refreshAutosurf(): Promise<void> {
  const summary = await api<AutosurfSummary>("/api/autosurf");
  byId("autosurf-credited").textContent = ltc(summary.creditedLifetimeLitoshi);
  byId("autosurf-credit-count").textContent = summary.creditCount.toLocaleString();
  byId("autosurf-fractional").textContent = `${summary.fractionalLitoshi.toFixed(6)} litoshi`;
  byId("autosurf-last-credit").textContent = summary.lastCreditAt
    ? new Date(summary.lastCreditAt * 1_000).toLocaleString()
    : "None yet";
  renderAutosurfCredits(summary.credits);
}

async function setupAutosurfPage(): Promise<void> {
  const sharePercent = settings.autosurfUserShareBps / 100;
  byId("autosurf-share").textContent = `${sharePercent.toLocaleString(undefined, { maximumFractionDigits: 2 })}%`;
  const link = byId<HTMLAnchorElement>("start-autosurf");
  if (!settings.autosurfEnabled) {
    byId("autosurf-state").textContent = "Setup pending";
    byId("autosurf-message").textContent = "Autosurf is currently unavailable.";
  } else {
    const session = await api<{ surfUrl: string }>("/api/autosurf/session", { method: "POST" });
    link.href = session.surfUrl;
    link.setAttribute("aria-disabled", "false");
    byId("autosurf-state").textContent = "Ready";
    byId("autosurf-message").textContent = "Credits may take a few minutes to arrive.";
    if (settings.autosurfLitoshiPerZer) {
      byId("autosurf-rate").textContent = `Current gross rate: ${settings.autosurfLitoshiPerZer.toLocaleString()} litoshi per ZER; your ${sharePercent.toLocaleString(undefined, { maximumFractionDigits: 2 })}% share is credited.`;
    }
  }
  await refreshAutosurf();
  byId("refresh-autosurf").addEventListener("click", () => void refreshAutosurf()
    .catch((error: Error) => { byId("autosurf-message").textContent = error.message; }));
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "visible") void refreshAutosurf().catch(() => undefined);
  });
  window.setInterval(() => {
    if (document.visibilityState === "visible") void refreshAutosurf().catch(() => undefined);
  }, 15_000);
}

function setAdminView(authenticated: boolean): void {
  byId("admin-login-view").hidden = authenticated;
  byId("admin-dashboard-view").hidden = !authenticated;
}

function renderAdminAdjustments(adjustments: AdminAdjustment[]): void {
  const container = byId("admin-adjustments");
  container.replaceChildren();
  if (adjustments.length === 0) {
    const empty = document.createElement("p");
    empty.className = "empty-state";
    empty.textContent = "No manual SDRP circulation adjustments have been recorded.";
    container.append(empty);
    return;
  }
  for (const adjustment of adjustments) {
    const row = document.createElement("div");
    row.className = "claim-row admin-ledger-row";
    const title = document.createElement("div");
    title.className = "claim-title";
    const note = document.createElement("strong");
    note.textContent = adjustment.note;
    const time = document.createElement("time");
    time.dateTime = new Date(adjustment.createdAt * 1_000).toISOString();
    time.textContent = new Date(adjustment.createdAt * 1_000).toLocaleString();
    title.append(note, time);
    const amount = document.createElement("span");
    amount.className = `claim-amount ${BigInt(adjustment.amountSdrpWei) < 0n ? "negative" : ""}`;
    amount.textContent = formatAdminSdrp(adjustment.amountSdrpWei);
    const permanent = document.createElement("span");
    permanent.className = "claim-status succeeded";
    permanent.textContent = "Permanent";
    row.append(title, amount, permanent);
    container.append(row);
  }
}

function renderAdminHostedFaucets(faucets: AdminHostedFaucet[]): void {
  const body = byId("admin-hosted-faucets");
  body.replaceChildren();
  byId("admin-hosted-empty").hidden = faucets.length > 0;
  byId("admin-hosted-table").hidden = faucets.length === 0;
  byId("admin-hosted-count").textContent = `${faucets.length} faucet${faucets.length === 1 ? "" : "s"}`;
  for (const faucet of faucets) {
    const row = document.createElement("tr");
    const name = document.createElement("th");
    name.scope = "row";
    const link = document.createElement("a");
    link.href = `/faucets/${encodeURIComponent(faucet.slug)}`;
    link.textContent = faucet.name;
    name.append(link);
    row.append(name);
    const values = [
      faucet.faucetpayUsername ?? "Unavailable",
      faucet.currency,
      `${100 - faucet.userPercent}% / ${faucet.userPercent}%`,
      ...[faucet.totalMicrolitoshi, faucet.ownerMicrolitoshi, faucet.creatorMicrolitoshi, faucet.usersMicrolitoshi]
        .map(value => formatAtomic(value, 14, 14, "LTC"))
    ];
    for (const value of values) {
      const cell = document.createElement("td");
      cell.textContent = value;
      row.append(cell);
    }
    body.append(row);
  }
}

function renderAdminDashboard(summary: AdminDashboard): void {
  byId("admin-outstanding-sdrp").textContent = summary.circulation.complete
    ? formatAdminSdrp(summary.circulation.totalOutstandingSdrpWei)
    : "Syncing…";
  byId("admin-profit").textContent = formatAdminLtc(summary.profit.totalMicrolitoshi);
  byId("admin-profit").classList.toggle("negative", BigInt(summary.profit.totalMicrolitoshi) < 0n);
  byId("admin-revenue").textContent = formatAdminLtc(summary.revenue.totalMicrolitoshi);
  byId("admin-user-earnings").textContent = formatAdminLtc(summary.userEarnings.totalMicrolitoshi);

  byId("admin-sdrp-site-payouts").textContent = formatAdminSdrp(summary.circulation.siteSdrpPayoutsWei);
  byId("admin-uniswap-buys").textContent = formatAdminSdrp(summary.circulation.uniswapGrossBoughtWei);
  byId("admin-uniswap-sells").textContent = formatAdminSdrp(summary.circulation.uniswapGrossSoldWei);
  byId("admin-uniswap-net").textContent = formatAdminSdrp(summary.circulation.uniswapNetBoughtWei);
  byId("admin-manual-total").textContent = formatAdminSdrp(summary.circulation.manualAdjustmentsWei);

  byId("admin-mining-revenue").textContent = formatAdminLtc(summary.revenue.miningMicrolitoshi);
  byId("admin-autosurf-revenue").textContent = formatAdminLtc(summary.revenue.autosurfMicrolitoshi);
  byId("admin-mining-earnings").textContent = formatAdminLtc(summary.userEarnings.miningMicrolitoshi);
  byId("admin-autosurf-earnings").textContent = formatAdminLtc(summary.userEarnings.autosurfMicrolitoshi);
  byId("admin-hosted-revenue").textContent = formatAdminLtc(summary.revenue.hostedMicrolitoshi);
  byId("admin-hosted-earnings").textContent = formatAdminLtc(summary.userEarnings.hostedMicrolitoshi);
  byId("admin-hosted-fee").textContent = formatAdminLtc(summary.profit.hostedFeeMicrolitoshi);
  byId("admin-gross-margin").textContent = formatAdminLtc(summary.profit.grossMarginMicrolitoshi);
  byId("admin-daily-faucet-expense").textContent = formatAdminLtc(summary.profit.dailyFaucetExpenseMicrolitoshi);
  byId("admin-owner-account-addback").textContent = formatAdminLtc(summary.profit.ownerAccountAddbackMicrolitoshi);
  byId("admin-net-profit").textContent = formatAdminLtc(summary.profit.totalMicrolitoshi);
  byId("admin-net-profit").classList.toggle("negative", BigInt(summary.profit.totalMicrolitoshi) < 0n);

  byId("admin-mining-settlements").textContent = summary.activity.miningSettlementCount.toLocaleString();
  byId("admin-zerads-reports").textContent = summary.activity.zeradsReportCount.toLocaleString();
  byId("admin-daily-claims").textContent = summary.activity.successfulDailyFaucetClaimCount.toLocaleString();
  byId("admin-review-count").textContent = summary.activity.operatorReviewCount.toLocaleString();
  byId("admin-review-count").classList.toggle("warning", summary.activity.operatorReviewCount > 0);
  byId("admin-last-earning").textContent = summary.activity.lastEarningAt
    ? new Date(summary.activity.lastEarningAt * 1_000).toLocaleString()
    : "None yet";
  byId("admin-revenue-scope").textContent = summary.revenue.scope;

  const chainState = byId("admin-chain-state");
  chainState.textContent = summary.uniswap.lastError
    ? "Sync issue"
    : summary.uniswap.caughtUp ? "Base synced" : "Catching up";
  chainState.classList.toggle("warning", Boolean(summary.uniswap.lastError) || !summary.uniswap.caughtUp);
  const indexed = summary.uniswap.indexedThroughBlock?.toLocaleString() ?? "not available";
  const confirmed = summary.uniswap.latestConfirmedBlock?.toLocaleString() ?? "not available";
  const chainDetail = `Pool ${summary.uniswap.poolAddress.slice(0, 8)}…${summary.uniswap.poolAddress.slice(-6)} · ${summary.uniswap.swapCount.toLocaleString()} swaps · indexed through block ${indexed} of confirmed head ${confirmed}.`;
  byId("admin-chain-details").textContent = summary.uniswap.lastError
    ? `${chainDetail} Last sync: ${summary.uniswap.lastError}`
    : chainDetail;
  renderAdminHostedFaucets(summary.hostedFaucets);
  renderAdminAdjustments(summary.adjustments);
  byId("admin-dashboard-message").textContent = `Updated ${new Date(summary.generatedAt * 1_000).toLocaleTimeString()}. Figures refresh every 30 seconds.`;
}

async function refreshAdminDashboard(): Promise<void> {
  const button = byId<HTMLButtonElement>("admin-refresh");
  button.disabled = true;
  byId("admin-dashboard-message").textContent = "Refreshing recorded economy figures…";
  try {
    const summary = await api<AdminDashboard>("/api/admin/dashboard");
    renderAdminDashboard(summary);
  } catch (error) {
    if (error instanceof ApiRequestError && error.code?.startsWith("admin_")) {
      setAdminView(false);
      byId("admin-login-message").textContent = error.message;
      return;
    }
    byId("admin-dashboard-message").textContent = error instanceof Error ? error.message : "Could not refresh the dashboard";
  } finally {
    button.disabled = false;
  }
}

async function loginAdmin(): Promise<void> {
  const input = byId<HTMLInputElement>("admin-password");
  const button = byId<HTMLButtonElement>("admin-login");
  button.disabled = true;
  byId("admin-login-message").textContent = "Checking password…";
  try {
    await api<{ authenticated: true }>("/api/admin/session", {
      method: "POST",
      body: JSON.stringify({ password: input.value })
    });
    input.value = "";
    setAdminView(true);
    await refreshAdminDashboard();
  } catch (error) {
    byId("admin-login-message").textContent = error instanceof Error ? error.message : "Login failed";
    input.select();
  } finally {
    button.disabled = false;
  }
}

async function logoutAdmin(): Promise<void> {
  await api("/api/admin/session", { method: "DELETE" }).catch(() => undefined);
  setAdminView(false);
  byId("admin-login-message").textContent = "Logged out.";
  byId<HTMLInputElement>("admin-password").focus();
}

async function addAdminAdjustment(): Promise<void> {
  const amount = byId<HTMLInputElement>("admin-adjustment-amount");
  const note = byId<HTMLInputElement>("admin-adjustment-note");
  const button = byId<HTMLButtonElement>("admin-add-adjustment");
  button.disabled = true;
  byId("admin-adjustment-message").textContent = "Recording permanent entry…";
  try {
    await api("/api/admin/circulation-adjustments", {
      method: "POST",
      body: JSON.stringify({ amountSdrp: amount.value, note: note.value })
    });
    amount.value = "";
    note.value = "";
    byId("admin-adjustment-message").textContent = "Permanent entry recorded.";
    await refreshAdminDashboard();
  } catch (error) {
    byId("admin-adjustment-message").textContent = error instanceof Error ? error.message : "Could not add the entry";
  } finally {
    button.disabled = false;
  }
}

async function setupAdminPage(): Promise<void> {
  byId("admin-login-form").addEventListener("submit", (event) => {
    event.preventDefault();
    void loginAdmin();
  });
  byId("admin-refresh").addEventListener("click", () => void refreshAdminDashboard());
  byId("admin-logout").addEventListener("click", () => void logoutAdmin());
  byId("admin-adjustment-form").addEventListener("submit", (event) => {
    event.preventDefault();
    void addAdminAdjustment();
  });
  try {
    await api("/api/admin/session");
    setAdminView(true);
    await refreshAdminDashboard();
  } catch {
    setAdminView(false);
  }
  window.setInterval(() => {
    if (document.visibilityState === "visible" && !byId("admin-dashboard-view").hidden) {
      void refreshAdminDashboard();
    }
  }, 30_000);
}

function selectPage(page: PageName): void {
  byId("home-page").hidden = page !== "home";
  byId("home-header-ad").hidden = page !== "home";
  byId("miner-page").hidden = page !== "miner";
  byId("faucet-page").hidden = page !== "faucet";
  byId("autosurf-page").hidden = page !== "autosurf";
  byId("sdrp-page").hidden = page !== "sdrp";
  byId("swap-page").hidden = page !== "swap";
  byId("admin-page").hidden = page !== "admin";
  document.querySelector<HTMLElement>(".site-nav")!.hidden = page === "admin";
  for (const candidate of ["home", "miner", "faucet", "autosurf", "sdrp", "swap"] as const) {
    const selected = page === candidate;
    byId(`nav-${candidate}`).classList.toggle("active", selected);
    byId(`nav-${candidate}`).setAttribute("aria-current", selected ? "page" : "false");
  }

}

async function initialize(): Promise<void> {
  if (["/promoted", "/submit"].includes(window.location.pathname)) {
    const { initializePromoted } = await import("./promoted");
    await initializePromoted(api, ensureAccount);
    return;
  }
  if (/^\/faucets(?:\/|$)/u.test(window.location.pathname)) {
    const { initializeHosted } = await import("./hosted");
    await initializeHosted();
    return;
  }
  const page: PageName = /^\/admin\/?$/u.test(window.location.pathname)
    ? "admin"
    : /^\/swap\/?$/u.test(window.location.pathname)
    ? "swap"
    : /^\/sdrp\/?$/iu.test(window.location.pathname)
    ? "sdrp"
    : /^\/autosurf\/?$/u.test(window.location.pathname)
      ? "autosurf"
      : /^\/faucet\/?$/u.test(window.location.pathname) ? "faucet" : /^\/mining\/?$/u.test(window.location.pathname) ? "miner" : "home";
  selectPage(page);
  byId(`${page}-page`).querySelectorAll<HTMLIFrameElement>("iframe[data-page-src]").forEach(frame => { frame.src = frame.dataset.pageSrc!; });
  if (page === "autosurf") {
    const banner = byId<HTMLIFrameElement>("autosurf-heading-ad");
    banner.src = banner.dataset.src!;
  }
  if (page === "miner") {
    // Device controls do not depend on account, balance or verification requests.
    setupThreads();
    const cpu = byId<HTMLInputElement>("cpu");
    const updateCpuLabel = () => { byId("cpu-value").textContent = `${cpu.value}%`; };
    updateCpuLabel();
    cpu.addEventListener("input", updateCpuLabel);
  }
  if (page === "swap") {
    byId("swap-page").querySelectorAll<HTMLIFrameElement>("iframe[data-src]").forEach((iframe) => {
      iframe.src = iframe.dataset.src!;
    });
    return;
  }
  if (page === "admin") {
    await setupAdminPage();
    return;
  }
  if (page === "sdrp") {
    return;
  }
  if (page === "home") {
    const banner = document.querySelector<HTMLIFrameElement>("[data-home-src]")!;
    banner.src = banner.dataset.homeSrc!;
    settings = await api<PublicConfig>("/api/config");
    await ensureAccount();
    await setupHomePage();
    return;
  }
  settings = await api<PublicConfig>("/api/config");
  byId("nav-faucet").hidden = !settings.faucetEnabled;
  if (page === "miner") setupWithdrawDialog();
  if (page === "faucet" && !settings.faucetEnabled) {
    window.location.replace("/");
    return;
  }
  if (page !== "autosurf") loadTraffic2Bitcoin(page === "faucet");
  await ensureAccount();

  if (page === "faucet") await setupFaucetPage();
  else if (page === "autosurf") await setupAutosurfPage();
  else await setupMiningPage();
}

void initialize().catch((error: Error) => {
  if (window.location.pathname.startsWith("/faucets")) {
    const target = document.getElementById("hosted-message");
    if (target) target.textContent = error.message;
    return;
  }
  const messageId = window.location.pathname === "/" ? "home-message" : /^\/admin\/?$/u.test(window.location.pathname)
    ? "admin-login-message"
    : /^\/autosurf\/?$/u.test(window.location.pathname)
    ? "autosurf-message"
    : /^\/faucet\/?$/u.test(window.location.pathname) ? "faucet-message" : "mining-message";
  byId(messageId).textContent = error.message;
});
